import {isNearAirport} from '@/utils/airports'
import {buildManifest} from '@/utils/manifest'
import {toPoint} from '@/utils/polyline'
import {resolveYyzTerminal} from '@/features/navigation/data/yyzArrivalEntries'

/**
 * WHO GETS THE ARRIVAL FLOW — the one place that answers it, for every screen that
 * shows a piece of it.
 *
 * ── ⚠️ THE WALK IS PEARSON'S, NOT "AN AIRPORT'S" ──────────────────────────────────
 * Every coordinate, every recorded step and every photograph under `features/navigation`
 * is YYZ Terminal 1 or Terminal 3. Shown at Billy Bishop or Vancouver they would be
 * confidently wrong — a passenger walked to a door that is not in the building they are
 * standing in — which is worse than showing nothing, because it looks like it works.
 *
 * ⚠️ AND IT IS ONLY THE FROM-AIRPORT DIRECTION. A passenger being driven TO an airport
 * has not landed, has no arrivals hall to cross and no booth to find; the whole flow is
 * about the walk from a gate to a kerb.
 *
 * ⚠️ THIS IS A STOPGAP FOR ONE AIRPORT, DELIBERATELY. The photographs and the navigation
 * steps are to come from the backend per airport, at which point the YYZ test below
 * becomes "does this airport have a published guide". Keeping the question in one
 * function is what makes that a single edit rather than a hunt.
 */

/** The only airport whose guide, photographs and indoor grids we actually have. */
export const ARRIVAL_GUIDE_AIRPORT_CODE = 'YYZ'

/**
 * The trip's pickup coordinate, or null.
 *
 * ⚠️ `origin` IS NOT RELIABLY AN OBJECT. api-docs types it as a string and some
 * endpoints send one; others send {lat, lng, address, …} — the same trap `locationLabel`
 * and `toPoint` exist to absorb (see utils/manifest). Read for `.lat` alone, a trip whose
 * origin arrived as a string has no pickup, and a real Pearson arrival would be turned
 * away from its own guide. So the manifest — which resolves the run's first drop from
 * waypoints, and from the solved route when those carry no coordinates — stands in.
 */
export function tripPickupPoint(trip) {
    return toPoint(trip?.origin) || buildManifest(trip)[0]?.point || null
}

export function isFromAirportTrip(trip) {
    return trip?.trip_direction === 'from_airport'
}

/**
 * Is this point Pearson?
 *
 * ⚠️ TWO TESTS, BECAUSE THEY FAIL IN OPPOSITE PLACES. The backend airport list is the
 * real answer, but it is fetched — read before it lands, every point is "not an airport".
 * The curb match needs no network and no list, but it only reaches 200m around the two
 * pickup doors, so it misses a pickup pinned on the airport's own centre. Either one
 * alone leaves a real YYZ trip without its guide.
 */
export function isArrivalGuideAirport(point) {
    if (!point) return false
    const airport = isNearAirport(point.lat, point.lng)
    if (airport) return String(airport.code || '').toUpperCase() === ARRIVAL_GUIDE_AIRPORT_CODE
    return resolveYyzTerminal([point.lng, point.lat]) !== null
}

/**
 * The welcome screen, the live walk and the written directions all hang off this one
 * answer.
 *
 * ── ⚠️ THE AIRPORT TEST IS OFF, ON REQUEST (2026-09-17) ───────────────────────────
 * It used to also require the pickup to BE Pearson — `isArrivalGuideAirport` above, which
 * is still exported and still correct. Every from-airport ride now gets the flow, wherever
 * it is picked up, because the portal is built and tested nowhere near Toronto and a test
 * booking in Lahore was being bounced to its own trip page.
 *
 * ⚠️ IT LIES EXACTLY LIKE `resolveTerminal`'S `T1` FALLBACK, and for the same reason. Every
 * coordinate, recorded step and photograph under `features/navigation` is YYZ Terminal 1 or
 * Terminal 3, so a passenger landing at Billy Bishop or Vancouver is now walked confidently
 * through a building they are not in. Restore the `&& isArrivalGuideAirport(...)` before
 * this reaches a real passenger outside Pearson.
 */
export function hasArrivalGuide(trip) {
    return isFromAirportTrip(trip)
}

export default hasArrivalGuide
